import React, { useState, useCallback, useEffect } from "react";
import { Save, Trash2, Crosshair } from "lucide-react";

/**
 * Full-screen overlay for visually tagging page elements.
 * When active, user clicks on elements to create tag markers.
 *
 * Props:
 *   active        – boolean to show/hide overlay
 *   onTagsChange  – callback with array of tags
 *   onDeactivate  – called when user exits tag mode
 */

function buildCssSelector(el) {
  const parts = [];
  let current = el;
  for (let i = 0; i < 4 && current && current !== document.body; i++) {
    let selector = current.tagName.toLowerCase();
    if (current.id) {
      selector += `#${current.id}`;
    } else if (current.className && typeof current.className === "string") {
      const classes = current.className.trim().split(/\s+/).slice(0, 2).join(".");
      if (classes) selector += `.${classes}`;
    }
    parts.unshift(selector);
    current = current.parentElement;
  }
  return parts.join(" > ");
}

export default function PageTagOverlay({ active, onTagsChange, onDeactivate }) {
  const [tags, setTags] = useState([]);
  const [editingTagId, setEditingTagId] = useState(null);

  // ESC to exit tag mode
  useEffect(() => {
    if (!active) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onDeactivate?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [active, onDeactivate]);

  // Sync tags up to parent
  useEffect(() => {
    onTagsChange?.(tags);
  }, [tags, onTagsChange]);

  const handleOverlayClick = useCallback((e) => {
    // Ignore clicks on tag markers or note inputs
    if (e.target.closest("[data-tag-ui]")) return;

    const overlay = e.currentTarget;

    // Temporarily disable pointer events on overlay to get element underneath
    overlay.style.pointerEvents = "none";
    const underlyingElement = document.elementFromPoint(e.clientX, e.clientY);
    overlay.style.pointerEvents = "auto";

    if (!underlyingElement || underlyingElement === document.body || underlyingElement === document.documentElement) return;

    const rect = underlyingElement.getBoundingClientRect();
    const newTag = {
      id: `tag-${Date.now()}`,
      x: e.clientX,
      y: e.clientY,
      pageX: e.clientX + window.scrollX,
      pageY: e.clientY + window.scrollY,
      elementText: (underlyingElement.textContent || "").trim().slice(0, 100),
      cssSelector: buildCssSelector(underlyingElement),
      tagName: underlyingElement.tagName.toLowerCase(),
      note: "",
      timestamp: new Date().toISOString(),
      elementRect: {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      },
    };

    setTags((prev) => [...prev, newTag]);
    setEditingTagId(newTag.id);
  }, []);

  const updateTagNote = useCallback((tagId, note) => {
    setTags((prev) => prev.map((t) => (t.id === tagId ? { ...t, note } : t)));
  }, []);

  const removeTag = useCallback((tagId) => {
    setTags((prev) => prev.filter((t) => t.id !== tagId));
    setEditingTagId(null);
  }, []);

  const saveTag = useCallback((tagId) => {
    setEditingTagId(null);
  }, []);

  if (!active) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] cursor-crosshair"
      onClick={handleOverlayClick}
      style={{ background: "rgba(0,0,0,0.05)" }}
    >
      {/* Instructions bar */}
      <div
        data-tag-ui="true"
        className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] px-4 py-2 rounded-xl bg-slate-900 border border-cyan-500/40 shadow-lg flex items-center gap-3"
      >
        <Crosshair size={14} className="text-cyan-400" />
        <span className="text-xs text-white font-bold">
          Click any element to tag it
        </span>
        <span className="text-[10px] text-slate-400">
          {tags.length} tag{tags.length !== 1 ? "s" : ""}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onDeactivate?.(); }}
          className="ml-2 px-3 py-1 rounded-lg bg-slate-700 text-slate-300 text-xs font-bold hover:bg-slate-600 transition-colors"
        >
          Done (ESC)
        </button>
      </div>

      {/* Tag markers */}
      {tags.map((tag) => (
        <div key={tag.id} data-tag-ui="true">
          {/* Red dot marker */}
          <div
            className="absolute w-4 h-4 rounded-full bg-red-500 border-2 border-white shadow-lg transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
            style={{ left: tag.x, top: tag.y }}
            onClick={(e) => { e.stopPropagation(); setEditingTagId(tag.id === editingTagId ? null : tag.id); }}
          />

          {/* Highlighted element outline */}
          {tag.elementRect && (
            <div
              className="absolute border-2 border-red-500/50 rounded pointer-events-none"
              style={{
                left: tag.elementRect.left,
                top: tag.elementRect.top,
                width: tag.elementRect.width,
                height: tag.elementRect.height,
              }}
            />
          )}

          {/* Note input popup */}
          {editingTagId === tag.id && (
            <div
              className="absolute z-[9999] w-64 rounded-xl bg-slate-900 border border-slate-600 shadow-2xl p-3 space-y-2"
              style={{
                left: Math.min(tag.x + 12, window.innerWidth - 280),
                top: Math.min(tag.y + 12, window.innerHeight - 200),
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <p className="text-[9px] font-mono text-slate-400 truncate">
                {tag.cssSelector}
              </p>
              {tag.elementText && (
                <p className="text-[10px] text-slate-500 truncate italic">
                  "{tag.elementText}"
                </p>
              )}
              <textarea
                value={tag.note}
                onChange={(e) => updateTagNote(tag.id, e.target.value)}
                placeholder="Add a note about this element..."
                className="w-full h-16 rounded-lg bg-slate-800 border border-slate-700 p-2 text-xs text-white placeholder-slate-500 resize-none outline-none focus:border-cyan-500/50"
                autoFocus
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={() => saveTag(tag.id)}
                  className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-cyan-500/20 text-cyan-400 text-[10px] font-bold hover:bg-cyan-500/30 transition-colors"
                >
                  <Save size={10} /> Save
                </button>
                <button
                  onClick={() => removeTag(tag.id)}
                  className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-red-500/20 text-red-400 text-[10px] font-bold hover:bg-red-500/30 transition-colors"
                >
                  <Trash2 size={10} /> Remove
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
